import { StickyHeader } from "@/components/shared/StickyHeader";
import { Hero } from "@/components/landing/Hero";
import { ValueProps } from "@/components/landing/ValueProps";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { FeaturesPreview } from "@/components/landing/FeaturesPreview";
import { FoundingMemberPerks } from "@/components/landing/FoundingMemberPerks";
import { SocialProof } from "@/components/landing/SocialProof";
import { WhosBehind } from "@/components/landing/WhosBehind";
import { FAQ } from "@/components/landing/FAQ";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { WaitlistFormSection } from "@/components/landing/WaitlistFormSection";
import { Footer } from "@/components/landing/Footer";
import { StickyMobileCTA } from "@/components/shared/StickyMobileCTA";
import { ExitIntentPopup } from "@/components/shared/ExitIntentPopup";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { usePageView } from "@/hooks/usePageView";
import { useEffect, useState, lazy, Suspense } from "react";
import { ComponentSkeleton } from "@/components/loading/ComponentSkeleton";

// Lazy-load heavy/conditional components for better performance
const SustainabilityProSection = lazy(() => import("@/components/landing/SustainabilityProSection"));
const EnterpriseSection = lazy(() => import("@/components/landing/EnterpriseSection"));

const Index = () => {
  // Track scroll depth for analytics
  useScrollDepth();
  
  // Track page views
  usePageView();

  // State for Sustainability Pro Section expansion
  const [isProSectionExpanded, setIsProSectionExpanded] = useState(false);
  
  // State for Enterprise Section expansion
  const [isEnterpriseExpanded, setIsEnterpriseExpanded] = useState(false);

  // Add skip to main content for accessibility
  useEffect(() => {
    const skipLink = document.getElementById("skip-to-main");
    if (skipLink) {
      skipLink.addEventListener("click", (e) => {
        e.preventDefault();
        const mainContent = document.getElementById("main-content");
        if (mainContent) {
          mainContent.focus();
          mainContent.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content link for screen readers */}
      <a
        id="skip-to-main"
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to main content
      </a>
      
      <StickyHeader />
      <main id="main-content" tabIndex={-1}>
        <Hero />
        <ValueProps />
        <HowItWorks 
          isExpanded={isProSectionExpanded} 
          setIsExpanded={setIsProSectionExpanded} 
        />
        <Suspense fallback={<ComponentSkeleton variant="compact" />}>
          <SustainabilityProSection isExpanded={isProSectionExpanded} />
        </Suspense>
        <FeaturesPreview />
        <FoundingMemberPerks />
        <SocialProof 
          isEnterpriseExpanded={isEnterpriseExpanded}
          setIsEnterpriseExpanded={setIsEnterpriseExpanded}
        />
        <div id="enterprise-section">
          <Suspense fallback={<ComponentSkeleton variant="compact" />}>
            <EnterpriseSection isExpanded={isEnterpriseExpanded} />
          </Suspense>
        </div>
        <WhosBehind />
        <FAQ />
        <FinalCTA />
        <WaitlistFormSection />
      </main>
      <Footer />
      <StickyMobileCTA />
      <ExitIntentPopup />
    </div>
  );
};

export default Index;
